import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Card, Button, Row, Col, Badge, Alert, Table, Form, InputGroup } from 'react-bootstrap';
import axios from '../../services/axiosConfig';
import * as XLSX from 'xlsx';
import { useAuth } from '../../context/AuthContext';
import { eventHasImage, getEventImageSrc, processEventImage } from '../../utils/imageUtils';
import { formatDate, formatDateRange } from '../../utils/dateUtils';

// Funzione helper per esportare dati in XLSX
const exportToXLSX = (data, filename = 'export.xlsx') => {
  try {
    if (!Array.isArray(data) || data.length === 0) {
      return;
    }

    // Normalizza le righe mantenendo l'ordine delle colonne desiderato
    const rows = data.map(item => ({
      Nome: item.name || '',
      Email: item.email || '',
      Telefono: item.phone || '',
      SDA: item.sda || '',
      'Data registrazione': item.registration_date ? new Date(item.registration_date).toLocaleDateString() : '',
      Stato: item.status === 'confirmed' ? 'Confermato' : (item.status === 'pending' ? 'In attesa' : 'Annullato')
    }));

    const headerOrder = ['Nome', 'Email', 'Telefono', 'SDA', 'Data registrazione', 'Stato'];
    const worksheet = XLSX.utils.json_to_sheet(rows, { header: headerOrder });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Registrazioni');
    XLSX.writeFile(workbook, filename);
  } catch (err) {
    console.error('Errore export XLSX:', err);
  }
};

const computeRegistrationStatus = (eventData) => {
  if (!eventData) return 'open';
  try {
    const now = new Date();
    const registrationDeadlineRaw = eventData.registrationDeadline || eventData.registration_deadline;
    const registrationDeadline = registrationDeadlineRaw ? new Date(registrationDeadlineRaw) : null;
    const allowLateRegistration = eventData.allowLateRegistration === true || eventData.allow_late_registration === true;
    const eventDate = new Date(eventData.eventDate || eventData.event_date);

    if (!isNaN(eventDate.getTime()) && now > eventDate) {
      return 'closed';
    }

    if (registrationDeadline && now > registrationDeadline) {
      return allowLateRegistration ? 'late' : 'closed';
    }

    return 'open';
  } catch (error) {
    console.error('Error checking registration status:', error);
    return 'open';
  }
};

const EventDetails = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [summaryParticipants, setSummaryParticipants] = useState([]);
  const [summaryTeams, setSummaryTeams] = useState([]);

  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        setLoading(true);
        // Fetch event details
        const eventResponse = await axios.get(`/api/events/${id}`);
        const eventData = eventResponse.data.event || eventResponse.data;
        // Process the event data to handle image properly
        const processedEvent = processEventImage(eventData);
        setEvent(processedEvent);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching event details:', err);
        setError('Impossibile caricare i dettagli dell\'evento. Riprova più tardi.');
        setLoading(false);
      }
    };
    fetchEventDetails();
  }, [id]);

  const registrationStatus = useMemo(() => computeRegistrationStatus(event), [event]);
  const isRegistrationOpen = registrationStatus !== 'closed';
  const isLateRegistrationOpen = registrationStatus === 'late';

  useEffect(() => {
    const fetchRegistrationSummary = async () => {
      if (!event || !user?.association_id) {
        setSummaryParticipants([]);
        setSummaryTeams([]);
        setSummaryError(null);
        setSummaryLoading(false);
        return;
      }

      try {
        setSummaryLoading(true);
        setSummaryError(null);

        const registrationMode = event.registrationMode || 'team';

        if (registrationMode === 'team') {
          const teamsResponse = await axios.get(
            `/api/associations/${user.association_id}/events/${id}/teams`
          );
          const teamsData = teamsResponse.data?.teams || teamsResponse.data || [];
          setSummaryTeams(Array.isArray(teamsData) ? teamsData : []);
          setSummaryParticipants([]);
        } else {
          const participantsResponse = await axios.get(
            `/api/associations/${user.association_id}/events/${id}/participants`
          );
          const participantsData = participantsResponse.data?.participants || participantsResponse.data || [];
          setSummaryParticipants(Array.isArray(participantsData) ? participantsData : []);
          setSummaryTeams([]);
        }
      } catch (err) {
        console.error('Error fetching registration summary:', err);
        setSummaryError('Impossibile caricare il riepilogo iscrizioni. Riprova piu tardi.');
      } finally {
        setSummaryLoading(false);
      }
    };

    fetchRegistrationSummary();
  }, [event, id, registrationStatus, user?.association_id]);

  if (loading) {
    return <div className="text-center mt-5"><p>Caricamento dettagli evento...</p></div>;
  }

  if (error) {
    return <Alert variant="danger" className="mt-4">{error}</Alert>;
  }

  if (!event) {
    return <Alert variant="warning" className="mt-4">Evento non trovato.</Alert>;
  }
  // Check if event is full
  const maxParticipants = event.maxParticipants || event.max_participants;
  const isFull = maxParticipants ? (event.participants_count >= maxParticipants) : false;
  const hasRegisteredParticipants = Number(event.participants_count || 0) > 0;
  const registrationMode = event.registrationMode || 'team';
  const isManifestazioneEvent = (event.category || '').toLowerCase() === 'manifestazione';
  const summaryStatusVariant = isLateRegistrationOpen
    ? 'warning'
    : (isRegistrationOpen ? 'success' : 'secondary');
  const summaryStatusText = isLateRegistrationOpen
    ? 'Iscrizioni tardive'
    : (isRegistrationOpen ? 'Iscrizioni aperte' : 'Iscrizioni chiuse');
  const summaryStatusTextColor = isLateRegistrationOpen ? 'dark' : undefined;

  const registrationBadgeClass = isLateRegistrationOpen
    ? 'bg-warning text-dark'
    : (isRegistrationOpen ? 'bg-success' : 'bg-danger');
  const registrationBadgeText = isLateRegistrationOpen
    ? 'Iscrizioni Tardive'
    : (isRegistrationOpen ? 'Iscrizioni Aperte' : 'Iscrizioni Chiuse');
  const registrationBadgeIcon = isLateRegistrationOpen
    ? 'bi-exclamation-triangle-fill'
    : (isRegistrationOpen ? 'bi-check-circle-fill' : 'bi-x-circle-fill');

  return (
    <Container className="mt-4">
      <Link to="/association" className="btn btn-outline-secondary mb-4">
        &larr; ritorna alla dashboard
      </Link>

      <Card className="mb-4">
        <Row className="g-0">
          {eventHasImage(event) ? (
            <Col md={4}>
              <div style={{
                height: '100%',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <Card.Img
                  src={getEventImageSrc(event)}
                  alt={event.title}
                  className="h-100"
                  style={{ objectFit: 'cover', width: '100%' }}
                  onError={(e) => {
                    // Only log the error once
                    console.error("Image failed to load for event:", event.title || event._id);
                    // Remove the image element to prevent repeated requests
                    e.target.style.display = 'none';
                    // Show fallback icon
                    const parent = e.target.parentElement;
                    if (parent) {
                      parent.innerHTML = `<div style="
                        height: 100%;
                        width: 100%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background-color: #f8f9fa;
                      "><i class="bi bi-calendar-event" style="font-size: 3rem; color: "#006e39;"></i></div>`;
                    }
                  }}
                />
              </div>
            </Col>
          ) : null}
          <Col md={eventHasImage(event) ? 8 : 12}>
            <Card.Body>
              <div className="d-flex flex-wrap justify-content-between align-items-start mb-3 gap-2">
                <Card.Title className="fs-2" style={{ color: '#006e39' }}>{event.title}</Card.Title>
                <span className={`badge ${registrationBadgeClass}`} 
                      style={{ fontSize: '1rem', padding: '10px 15px' }}>
                  <i className={`bi ${registrationBadgeIcon} me-2`}></i>
                  {registrationBadgeText}
                </span>
              </div>
              
              <Card.Subtitle className="mb-3 text-muted">
                <i className="bi bi-calendar3 me-2"></i>
                {formatDateRange(event.eventDate || event.event_date, event.eventEndDate || event.event_end_date || event.eventDate || event.event_date)}
                {(event.startTime || event.start_time) && ` - ${event.startTime || event.start_time}`}
                {(event.endTime || event.end_time) && ` alle ${event.endTime || event.end_time}`}
              </Card.Subtitle>

              <div className="mb-3 d-flex flex-wrap gap-2">
                {event.category && (
                  <Badge bg="primary" style={{ fontSize: '0.9rem' }}>{event.category}</Badge>
                )}
                {event.sda && (
                  <Badge bg="info" style={{ fontSize: '0.9rem' }}>{event.sda}</Badge>
                )}
                {(() => {
                  const scopeLabels = { national: 'Nazionale', regional: 'Regionale', territorial: 'Territoriale' };
                  const scopeBg = { national: 'danger', regional: 'warning', territorial: 'secondary' };
                  const normalizedScope = String(event.scope || 'territorial').toLowerCase();
                  const label = scopeLabels[normalizedScope] || 'Non specificato';
                  const bg = scopeBg[normalizedScope] || 'light';
                  return (
                    <Badge bg={bg} style={{ fontSize: '0.9rem' }}>
                      {label}
                    </Badge>
                  );
                })()}
              </div>

              <Card.Text className="mb-4">{event.description}</Card.Text>

              <Row className="mb-4">
                <Col sm={6}>
                  <h5><i className="bi bi-geo-alt-fill text-danger me-2"></i>Località</h5>
                  <p>{event.location || 'Non specificata'}</p>
                </Col>
              </Row>

              {/* Chiusura iscrizioni */}
              {event.registrationDeadline && (
                <Row className="mb-4">
                  <Col sm={12}>
                    <div className="alert alert-info d-flex align-items-center" role="alert">
                      <i className="bi bi-calendar-x me-2" style={{ fontSize: '1.5rem' }}></i>
                      <div>
                        <strong>Chiusura iscrizioni:</strong>{' '}
                        {new Date(event.registrationDeadline).toLocaleString('it-IT', {
                          dateStyle: 'full',
                          timeStyle: 'short'
                        })}
                      </div>
                    </div>
                  </Col>
                </Row>
              )}

              {/* Prezzo dell'evento */}
              {/* {event.price !== undefined && event.price > 0 && (
                <h5 className="mb-3"><i className="bi bi-currency-euro text-warning me-2"></i>Prezzo: €{Number(event.price).toFixed(2)}</h5>
              )} */}

              {/* Modalità di iscrizione */}
              {event.registrationMode && (
                <div className="mb-4">
                  <h5>Modalità di iscrizione</h5>
                  <div className="d-flex align-items-center p-2 rounded"
                    style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                    <i className={`bi ${event.registrationMode === 'team' ? 'bi-people-fill' : 'bi-person'} me-2`}
                      style={{ color: '#006e39', fontSize: '1.25rem' }}></i>
                    <div>
                      <strong>
                        {event.registrationMode === 'team' ? 'Iscrizione squadre' : 'Iscrizione individuale'}
                      </strong>
                      <div className="small text-muted">
                        {event.registrationMode === 'team'
                          ? 'L\'associazione iscrive una o più squadre/formazioni/gruppi.'
                          : 'L\'associazione iscrive i singoli soci partecipanti all\'evento.'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Documenti e regolamenti */}
              {(event.regulationDocument || (event.documents && event.documents.length > 0)) && (
                <div className="mb-4">
                  <h5 className="mb-3">Documenti e Regolamenti</h5>
                  <div className="d-flex flex-wrap gap-2">
                    {/* Mostra il documento di regolamento principale se esiste */}
                    {event.regulationDocument && (
                      <a
                        href={`/api/events/${event._id}/documents/regulation`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none"
                      >
                        <div className="border rounded p-3 d-flex align-items-center">
                          <div className="me-2 d-inline-flex justify-content-center align-items-center bg-danger bg-opacity-10 p-2 rounded-circle">
                            <i className="bi bi-file-earmark-pdf" style={{ fontSize: '1.2rem', color: '#dc3545' }}></i>
                          </div>
                          <div>
                            <strong>Regolamento</strong>
                            <small className="d-block text-muted text-truncate" style={{ maxWidth: "200px" }}>
                              {event.regulationDocument.name || 'Regolamento dell\'evento'}
                            </small>
                          </div>
                        </div>
                      </a>
                    )}

                    {/* Mostra gli altri documenti allegati */}
                    {event.documents && event.documents.map((doc, index) => (
                      <a
                        key={doc._id || index}
                        href={`/api/events/${event._id}/documents/${doc._id != null ? doc._id : index}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none"
                      >
                        <div className="border rounded p-3 d-flex align-items-center">
                          <div className="me-2 d-inline-flex justify-content-center align-items-center bg-primary bg-opacity-10 p-2 rounded-circle">
                            <i className={`bi ${doc.contentType?.includes('pdf') ? 'bi-file-earmark-pdf' : 'bi-file-earmark-text'}`}
                              style={{ fontSize: '1.2rem', color: '#0d6efd' }}></i>
                          </div>
                          <div>
                            <strong>Documento</strong>
                            <small className="d-block text-muted text-truncate" style={{ maxWidth: "200px" }}>
                              {doc.name || `Documento ${index + 1}`}
                            </small>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Campi di registrazione personalizzati */}
              {event.registrationFields && event.registrationFields.length > 0 && (
                <div className="mb-4">
                  <h5 className="mb-3">Informazioni richieste per l'iscrizione</h5>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead className="table-light">
                        <tr>
                          <th>Campo</th>
                          <th>Tipo</th>
                          <th>Obbligatorio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {event.registrationFields.map((field, index) => (
                          <tr key={field._id || index}>
                            <td>
                              <strong>{field.fieldLabel}</strong>
                              {field.options && field.options.length > 0 && (
                                <small className="d-block text-muted">
                                  {field.options.length} opzioni disponibili
                                </small>
                              )}
                            </td>
                            <td>
                              <Badge bg="secondary">
                                {field.fieldType === 'text' ? 'Testo' :
                                  field.fieldType === 'select' ? 'Selezione' :
                                    field.fieldType === 'date' ? 'Data' : field.fieldType}
                              </Badge>
                            </td>
                            <td>
                              {field.isRequired ? (
                                <Badge bg="danger">Sì</Badge>
                              ) : (
                                <Badge bg="success">No</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Pulsanti di registrazione */}
              {isRegistrationOpen ? (
                <div className="d-flex flex-wrap justify-content-end gap-2">
                  {/* Usa il valore di registrationMode per determinare il pulsante corretto */}
                  {(event.registrationMode === 'individual' || !event.registrationMode) && (
                    <Button
                      variant="outline-primary"
                      size="lg"
                      onClick={() => window.location.href = `/association/events/${event._id}/select-members`}
                      style={{ backgroundColor: "transparent", borderColor: "#006e39", color: "#006e39" }}
                    >
                      <i className="bi bi-person-plus me-2"></i>
                      {hasRegisteredParticipants ? 'Modifica iscrizioni' : 'Seleziona soci individuali'}
                    </Button>
                  )}
                  {event.registrationMode === 'team' && (
                    <>
                      <Button
                        variant="outline-secondary"
                        size="lg"
                        onClick={() => window.location.href = `/association/events/${event._id}/teams`}
                      >
                        <i className="bi bi-list-ul me-2"></i>
                        Visualizza squadre
                      </Button>
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={() => window.location.href = `/association/events/${event._id}/team-registration`}
                        style={{ backgroundColor: "#006e39", borderColor: "#006e39" }}
                      >
                        <i className="bi bi-people-fill me-2"></i>
                        Iscrivi squadra
                      </Button>
                    </>
                  )}
                </div>
              ) : (
                <Alert variant="danger" className="mt-3">
                  <h5 className="alert-heading">
                    <i className="bi bi-x-circle-fill me-2"></i>
                    Le iscrizioni sono chiuse
                  </h5>
                  <p className="mb-0">
                    Il termine per le iscrizioni a questo evento è scaduto. 
                    Per ulteriori informazioni, contatta il comitato di riferimento.
                  </p>
                </Alert>
              )}

              <Card className="mt-4 border-0 shadow-sm">
                <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <h5 className="mb-0">
                    <i className="bi bi-clipboard-check me-2"></i>
                    Riepilogo iscrizioni
                  </h5>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <Badge bg={summaryStatusVariant} text={summaryStatusTextColor}>
                      {summaryStatusText}
                    </Badge>
                    {registrationMode === 'team' && summaryTeams.length > 0 && (
                      <>
                        <Badge bg="info">
                          {summaryTeams.length} {isManifestazioneEvent ? 'gruppi' : 'squadre'}
                        </Badge>
                        <Badge bg="success">
                          {summaryTeams.reduce((acc, team) => acc + (team.members ? team.members.length : 0), 0)} partecipanti
                        </Badge>
                      </>
                    )}
                    {registrationMode === 'individual' && summaryParticipants.length > 0 && (
                      <Badge bg="info">
                        {summaryParticipants.length} partecipanti iscritti
                      </Badge>
                    )}
                  </div>
                </Card.Header>
                <Card.Body>
                  {isRegistrationOpen && (
                    <div className="text-muted small mb-3">
                      Le iscrizioni sono aperte: il riepilogo mostra le registrazioni attuali.
                    </div>
                  )}
                  {summaryLoading ? (
                      <div className="text-center py-3">
                        <div className="spinner-border text-success" role="status">
                          <span className="visually-hidden">Caricamento...</span>
                        </div>
                        <p className="mt-2 mb-0">Caricamento riepilogo...</p>
                      </div>
                    ) : summaryError ? (
                      <Alert variant="danger" className="mb-0">{summaryError}</Alert>
                    ) : registrationMode === 'team' ? (
                      summaryTeams.length === 0 ? (
                        <Alert variant="info" className="mb-0">
                          Nessuna {isManifestazioneEvent ? 'iscrizione di gruppo' : 'squadra iscritta'} per questa associazione.
                        </Alert>
                      ) : (
                        <div className="table-responsive">
                          <Table striped bordered hover className="mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>{isManifestazioneEvent ? 'Gruppo/Partecipanti' : 'Squadra'}</th>
                                <th>Data iscrizione</th>
                                <th>Partecipanti</th>
                              </tr>
                            </thead>
                            <tbody>
                              {summaryTeams.map((team, index) => (
                                <tr key={team._id || team.id || index}>
                                  <td>{team.name || `${isManifestazioneEvent ? 'Gruppo' : 'Squadra'} ${index + 1}`}</td>
                                  <td>{formatDate(team.registration_date || team.created_at || team.createdAt)}</td>
                                  <td>{team.members ? team.members.length : 0}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )
                    ) : summaryParticipants.length === 0 ? (
                      <Alert variant="info" className="mb-0">
                        Nessun partecipante iscritto per questa associazione.
                      </Alert>
                    ) : (
                      <div className="table-responsive">
                        <Table striped bordered hover className="mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Nome</th>
                              <th>Email</th>
                              <th>Telefono</th>
                              <th>SDA</th>
                              <th>Data iscrizione</th>
                              <th>Stato</th>
                            </tr>
                          </thead>
                          <tbody>
                            {summaryParticipants.map((participant, index) => {
                              const participantName = participant.name
                                || [participant.first_name, participant.last_name].filter(Boolean).join(' ')
                                || 'N/A';
                              const participantStatus = participant.status === 'confirmed'
                                ? 'Confermato'
                                : participant.status === 'pending'
                                  ? 'In attesa'
                                  : participant.status === 'cancelled'
                                    ? 'Annullato'
                                    : (participant.status || 'N/D');

                              return (
                                <tr key={participant.id || participant._id || index}>
                                  <td>{participantName}</td>
                                  <td>{participant.email || '-'}</td>
                                  <td>{participant.phone || '-'}</td>
                                  <td>{participant.sda || '-'}</td>
                                  <td>{formatDate(participant.registration_date || participant.created_at || participant.createdAt)}</td>
                                  <td>{participantStatus}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    )}
                </Card.Body>
              </Card>

              {isLateRegistrationOpen && (
                <Alert variant="warning" className="mt-3">
                  <h5 className="alert-heading">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    Iscrizioni tardive attive
                  </h5>
                  <p className="mb-0">
                    Il termine ordinario di iscrizione e scaduto, ma il comitato ha abilitato le iscrizioni tardive.
                  </p>
                </Alert>
              )}
            </Card.Body>
          </Col>
        </Row>
      </Card>
    </Container>
  );
};

export default EventDetails;